"use client";

import React, { useEffect, useMemo, useState } from "react";
import type { TradeOrder } from "@/vybe-trader-client/trade-history/types";
import type { AccountAnalytics } from "@/vybe-trader-client/trade-history/accountAnalytics";
import {
  computeMonthlyDrilldown,
  formatDurationShort,
} from "@/vybe-trader-client/trade-history/accountAnalytics";
import { cn } from "@/lib/utils";
import {
  AnalyticsCard,
  AnalyticsCardHeader,
  AnalyticsTab,
  MONTH_LABELS,
  fmtRatio,
  fmtSignedMoney,
  fmtSignedPct,
} from "./analyticsShared";

/**
 * Monthly Analytics — per-year gain bars; clicking a month redraws the
 * per-symbol Reward:Risk and average holding time (long vs short) panels.
 */
export default function AnalyticsMonthlyCard({
  analytics,
  positions,
  currency,
}: {
  analytics: AccountAnalytics;
  positions: TradeOrder[];
  currency: string;
}) {
  const years = analytics.years;
  const latestYear = years.length > 0 ? years[years.length - 1] : null;
  const [year, setYear] = useState<number | null>(latestYear);
  const activeYear = year !== null && years.includes(year) ? year : latestYear;

  const yearMonths = useMemo(
    () => analytics.monthly.filter((m) => m.year === activeYear),
    [analytics.monthly, activeYear]
  );

  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const activeMonth =
    selectedMonth !== null && yearMonths.some((m) => m.month === selectedMonth)
      ? selectedMonth
      : yearMonths.length > 0
        ? yearMonths[yearMonths.length - 1].month
        : null;

  useEffect(() => {
    // Reset the drilldown when the year tab changes.
    setSelectedMonth(null);
  }, [activeYear]);

  const drilldown = useMemo(() => {
    if (activeYear === null || activeMonth === null) return null;
    return computeMonthlyDrilldown(positions, activeYear, activeMonth);
  }, [positions, activeYear, activeMonth]);

  const maxAbs = useMemo(
    () =>
      yearMonths.reduce(
        (m, b) => Math.max(m, Math.abs(b.gainPct ?? 0), 0.0001),
        0.0001
      ),
    [yearMonths]
  );

  if (years.length === 0) {
    return (
      <AnalyticsCard>
        <AnalyticsCardHeader>
          <span>Monthly analytics</span>
        </AnalyticsCardHeader>
        <div className="flex h-16 items-center justify-center text-[11px] text-muted-foreground">
          No closed trades in this scope
        </div>
      </AnalyticsCard>
    );
  }

  const monthTitle =
    activeMonth !== null && activeYear !== null
      ? `${MONTH_LABELS[activeMonth]} ${activeYear}`
      : "—";

  return (
    <AnalyticsCard>
      <AnalyticsCardHeader>
        <span className="mr-1">Monthly analytics</span>
        {years.map((y) => (
          <AnalyticsTab key={y} active={y === activeYear} onClick={() => setYear(y)}>
            {y}
          </AnalyticsTab>
        ))}
        <span className="ml-auto normal-case tracking-normal">
          click a month to drill down
        </span>
      </AnalyticsCardHeader>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px]">
        {/* month gain bars */}
        <div className="flex items-end gap-2 px-3 pb-2 pt-3" style={{ minHeight: 150 }}>
          {yearMonths.map((m) => {
            const gain = m.gainPct ?? 0;
            const h = Math.max(4, (Math.abs(gain) / maxAbs) * 92);
            const on = m.month === activeMonth;
            return (
              <button
                key={m.month}
                type="button"
                onClick={() => setSelectedMonth(m.month)}
                className="group flex flex-1 flex-col items-center justify-end gap-1 self-stretch"
                title={`${MONTH_LABELS[m.month]} ${m.year} — ${
                  m.gainPct === null
                    ? fmtSignedMoney(m.pnl, currency)
                    : fmtSignedPct(m.gainPct)
                } · ${m.trades} trades`}
              >
                <span
                  className={cn(
                    "font-mono text-[9px] tabular-nums",
                    gain >= 0 ? "text-blue-400" : "text-red-400"
                  )}
                >
                  {m.gainPct === null ? "—" : fmtSignedPct(m.gainPct, 1)}
                </span>
                <span
                  className={cn(
                    "w-6 rounded-t-sm transition-opacity",
                    gain >= 0 ? "bg-blue-400" : "bg-red-400",
                    on
                      ? "opacity-100 ring-1 ring-ring"
                      : "opacity-50 group-hover:opacity-80"
                  )}
                  style={{ height: h }}
                />
                <span className="font-mono text-[9px] text-muted-foreground">
                  {MONTH_LABELS[m.month]}
                </span>
              </button>
            );
          })}
        </div>

        {/* drilldown */}
        <div className="border-t border-border lg:border-l lg:border-t-0">
          <DrillTitle>Reward : Risk — {monthTitle}</DrillTitle>
          {!drilldown || drilldown.rewardRisk.length === 0 ? (
            <DrillEmpty />
          ) : (
            <div className="py-1">
              {drilldown.rewardRisk.map((r) => {
                const maxRatio = drilldown.rewardRisk.reduce(
                  (m, x) => Math.max(m, x.ratio ?? 0),
                  1
                );
                const w =
                  r.ratio !== null ? Math.max(4, (r.ratio / maxRatio) * 100) : 0;
                return (
                  <div
                    key={r.symbol}
                    className="flex items-center gap-2 px-2.5 py-1 font-mono text-[10px]"
                  >
                    <span className="w-16 shrink-0 text-foreground">
                      {r.symbol}
                    </span>
                    <span className="h-2 flex-1 overflow-hidden rounded-sm bg-muted">
                      <span
                        className="block h-full bg-blue-400"
                        style={{ width: `${w}%` }}
                      />
                    </span>
                    <span className="w-12 shrink-0 text-right text-muted-foreground">
                      {fmtRatio(r.ratio)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          <DrillTitle className="border-t border-border/60">
            Avg holding time — {monthTitle}
          </DrillTitle>
          {!drilldown || drilldown.holdingTimes.length === 0 ? (
            <DrillEmpty />
          ) : (
            <div className="py-1">
              {drilldown.holdingTimes.map((h) => {
                const maxMs = drilldown.holdingTimes.reduce(
                  (m, x) => Math.max(m, x.longMs ?? 0, x.shortMs ?? 0),
                  1
                );
                return (
                  <div
                    key={h.symbol}
                    className="flex items-center gap-2 px-2.5 py-1 font-mono text-[10px]"
                  >
                    <span className="w-16 shrink-0 text-foreground">
                      {h.symbol}
                    </span>
                    <span className="flex flex-1 flex-col gap-0.5">
                      <span className="h-1.5 overflow-hidden rounded-sm bg-muted">
                        <span
                          className="block h-full bg-blue-400"
                          style={{
                            width: `${((h.longMs ?? 0) / maxMs) * 100}%`,
                          }}
                        />
                      </span>
                      <span className="h-1.5 overflow-hidden rounded-sm bg-muted">
                        <span
                          className="block h-full bg-red-400"
                          style={{
                            width: `${((h.shortMs ?? 0) / maxMs) * 100}%`,
                          }}
                        />
                      </span>
                    </span>
                    <span className="w-20 shrink-0 text-right text-muted-foreground">
                      {h.longMs !== null ? formatDurationShort(h.longMs) : "—"}
                      {" / "}
                      {h.shortMs !== null ? formatDurationShort(h.shortMs) : "—"}
                    </span>
                  </div>
                );
              })}
              <div className="flex items-center gap-3 px-2.5 py-1 text-[9px] text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <span className="h-2 w-2 rounded-sm bg-blue-400" /> long
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="h-2 w-2 rounded-sm bg-red-400" /> short
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </AnalyticsCard>
  );
}

function DrillTitle({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex h-6 items-center border-b border-border/60 px-2.5",
        "font-mono text-[9.5px] uppercase tracking-wide text-muted-foreground",
        className
      )}
    >
      {children}
    </div>
  );
}

function DrillEmpty() {
  return (
    <div className="flex h-10 items-center justify-center text-[10px] text-muted-foreground">
      No trades this month
    </div>
  );
}
